import React, { useState, useEffect } from 'react';
import { useSession } from '@/context/SessionContext';
import LearnerLayout from '@/components/LearnerLayout';
import { Loader2, Target, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LearnerProgrammePage = () => {
  const { selectedSessionId, currentCourse } = useSession();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedSessionId) { setLoading(false); return; }
    fetch(`${API}/courses/${selectedSessionId}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setCourse(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedSessionId]);

  const courseTitle = course?.title || currentCourse?.title || 'Formation';
  const description = course?.description || currentCourse?.description || '';
  const modules = course?.modules || currentCourse?.modules || [];
  const totalHours = modules.reduce((sum, m) => sum + (parseInt(m.duration) || 0), 0);
  const objectives = course?.objectives || currentCourse?.objectives || [];

  if (loading) {
    return <LearnerLayout><div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div></LearnerLayout>;
  }

  return (
    <LearnerLayout>
      <div data-testid="learner-programme-page">
        <p className="text-sm text-slate-400 mb-6">{courseTitle} - Programme</p>

        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-3 leading-tight" data-testid="programme-title">
          {courseTitle}
        </h1>

        <p className="text-slate-500 italic mb-6">Formation présentielle</p>

        <div className="border-t border-slate-200 pt-6 mb-8">
          <p className="text-slate-700 leading-relaxed max-w-4xl">{description || 'Cette formation vise à accompagner les participants dans la prise en main de leurs responsabilités, le pilotage de projets, et le développement de compétences stratégiques.'}</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left: Objectifs + Durée */}
          <div className="lg:w-96 shrink-0 space-y-6">
            {totalHours > 0 && (
              <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 rounded-full px-5 py-2.5" data-testid="duration-badge">
                <span className="font-bold">Durée:</span>
                <span>{totalHours} heures {modules.length} jours</span>
              </div>
            )}

            <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="objectives-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                  <Target className="h-5 w-5 text-white" />
                </div>
                <h2 className="font-bold text-slate-900">Objectifs pédagogiques</h2>
              </div>
              <ul className="space-y-3">
                {objectives.length > 0 ? objectives.map((obj, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="text-slate-400 mt-0.5">&#9656;</span>
                    <span>{obj}</span>
                  </li>
                )) : (
                  <>
                    <li className="flex items-start gap-2 text-sm text-slate-700"><span className="text-slate-400 mt-0.5">&#9656;</span><span>Connaître les enjeux éducatifs et pédagogiques à piloter aujourd'hui.</span></li>
                    <li className="flex items-start gap-2 text-sm text-slate-700"><span className="text-slate-400 mt-0.5">&#9656;</span><span>Concevoir, conduire et évaluer des projets à dimension éducative et pédagogique.</span></li>
                    <li className="flex items-start gap-2 text-sm text-slate-700"><span className="text-slate-400 mt-0.5">&#9656;</span><span>Développer un leadership pédagogique.</span></li>
                    <li className="flex items-start gap-2 text-sm text-slate-700"><span className="text-slate-400 mt-0.5">&#9656;</span><span>S'approprier des stratégies et des techniques d'animation.</span></li>
                    <li className="flex items-start gap-2 text-sm text-slate-700"><span className="text-slate-400 mt-0.5">&#9656;</span><span>Organiser l'information et la communication auprès des équipes.</span></li>
                    <li className="flex items-start gap-2 text-sm text-slate-700"><span className="text-slate-400 mt-0.5">&#9656;</span><span>Analyser le fonctionnement des équipes, gérer les relations.</span></li>
                  </>
                )}
              </ul>
            </div>
          </div>

          {/* Right: Contenu de la formation */}
          <div className="flex-1">
            <h2 className="text-2xl font-extrabold text-slate-900 mb-6" data-testid="content-title">Contenu de la formation</h2>
            <div className="space-y-8">
              {modules.length > 0 ? modules.map((mod, i) => (
                <div key={i} data-testid={`module-${i}`}>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">
                    Jour {i + 1} : {mod.date || ''} - {mod.title || mod.name}
                  </h3>
                  {mod.content ? (
                    <div className="space-y-3 pl-4">
                      {mod.content.split('\n').filter(Boolean).map((line, j) => (
                        <div key={j} className="flex items-start gap-2 text-sm text-slate-700">
                          <Check className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                          <span>{line.trim()}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 pl-4">{mod.description || 'Contenu à venir'}</p>
                  )}
                </div>
              )) : (
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">Jour 1 : Piloter la dimension éducative et pédagogique de la mission</h3>
                  <div className="space-y-3 pl-4">
                    <div className="flex items-start gap-2 text-sm text-slate-700"><Check className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" /><span>Matin (3h) : "ma lettre de mission et mon établissement : décryptage et premières actions."</span></div>
                    <div className="flex items-start gap-2 text-sm text-slate-700"><Check className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" /><span>Après-midi (3h) : "les priorités éducatives : des intuitions à l'action."</span></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </LearnerLayout>
  );
};

export default LearnerProgrammePage;
